import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import supabase from '../../../utils/supabase';
import useProfileCompletion from '../hooks/useProfileCompletion';
import { formatProfileData, formatPortfolioItems } from '../utils/dataFormatters';

// Create context
const PortfolioContext = createContext();

/**
 * Portfolio Provider component to manage portfolio data state
 * @param {Object} props - Component props
 * @param {string} props.userId - User ID to fetch portfolio data for
 * @param {React.ReactNode} props.children - Child components
 */
export const PortfolioProvider = ({ userId, children }) => {
  // State for portfolio data
  const [isLoading, setIsLoading] = useState(true);
  const [profileData, setProfileData] = useState(null);
  const [portfolioItems, setPortfolioItems] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [error, setError] = useState(null);

  // Function to fetch portfolio data
  const fetchPortfolioData = async () => {
    if (!userId) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      
      // Fetch user profile from user_profiles table directly (gets latest data)
      const { data: userProfile, error: profileError } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single();
          
      if (profileError) throw profileError;
      
      // Fetch portfolio items
      const { data: portfolioItems, error: portfolioError } = await supabase
        .from('portfolio_items')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });
          
      if (portfolioError) throw portfolioError;
      
      // Fetch reviews (if applicable)
      // For now, using mock data or empty array
      const mockReviews = [];
      
      // Format profile data with utility function
      const formattedProfile = formatProfileData(userProfile);
      const formattedPortfolioItems = formatPortfolioItems(portfolioItems || []);
      
      // Add portfolio items to profile data for easy access by components
      formattedProfile.portfolio_items = formattedPortfolioItems;
      
      setProfileData(formattedProfile);
      setPortfolioItems(formattedPortfolioItems);
      setReviews(mockReviews);
      setError(null);
    } catch (err) {
      console.error('Error fetching portfolio data:', err);
      setError(err.message || 'Failed to load portfolio data');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch portfolio data on component mount or when userId changes
  useEffect(() => {
    fetchPortfolioData();
  }, [userId]);

  // Subscribe to real-time user profile updates (for AI assessment skills sync)
  useEffect(() => {
    if (!userId) return;

    console.log('[PortfolioContext] Setting up real-time listener for user profile changes');
    
    const channel = supabase
      .channel(`public:user_profiles:id=eq.${userId}`)
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'user_profiles',
        filter: `id=eq.${userId}`
      }, (payload) => {
        console.log('[PortfolioContext] User profile updated, refreshing data:', payload);
        fetchPortfolioData();
      })
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          console.log('[PortfolioContext] Subscribed to user_profiles changes');
        }
      });

    return () => {
      console.log('[PortfolioContext] Unsubscribing from user_profiles changes');
      channel.unsubscribe();
    };
  }, [userId]);

  // Calculate profile completion percentage
  const completionPercentage = useProfileCompletion(profileData);

  // Filter portfolio items
  const publicItems = useMemo(() => {
    return portfolioItems.filter(item => item.is_public);
  }, [portfolioItems]);

  const featuredItems = useMemo(() => {
    return portfolioItems.filter(item => item.is_featured);
  }, [portfolioItems]);

  // Portfolio metrics
  const metrics = useMemo(() => {
    return {
      totalItems: portfolioItems.length,
      publicItems: publicItems.length,
      featuredItems: featuredItems.length,
      completionPercentage
    };
  }, [portfolioItems, publicItems, featuredItems, completionPercentage]);

  // Update portfolio item
  const updatePortfolioItem = async (itemId, updatedData) => {
    try {
      const { data, error } = await supabase
        .from('portfolio_items')
        .update(updatedData)
        .eq('id', itemId)
        .select();
        
      if (error) throw error;
      
      // Update local state
      setPortfolioItems(prev => 
        prev.map(item => item.id === itemId ? data[0] : item)
      );
      
      return { success: true, data };
    } catch (err) {
      console.error('Error updating portfolio item:', err);
      return { success: false, error: err.message };
    }
  };

  // Add portfolio item
  const addPortfolioItem = async (newItemData) => {
    try {
      const { data, error } = await supabase
        .from('portfolio_items')
        .insert({ ...newItemData, user_id: userId })
        .select();
        
      if (error) throw error;
      
      // Update local state
      setPortfolioItems(prev => [data[0], ...prev]);
      
      return { success: true, data };
    } catch (err) {
      console.error('Error adding portfolio item:', err);
      return { success: false, error: err.message };
    }
  };

  // Delete portfolio item
  const deletePortfolioItem = async (itemId) => {
    try {
      const { error } = await supabase
        .from('portfolio_items')
        .delete()
        .eq('id', itemId);
        
      if (error) throw error;
      
      // Update local state
      setPortfolioItems(prev => prev.filter(item => item.id !== itemId));
      
      return { success: true };
    } catch (err) {
      console.error('Error deleting portfolio item:', err);
      return { success: false, error: err.message };
    }
  };

  // Context value
  const value = {
    isLoading,
    error,
    profileData,
    portfolioItems,
    publicItems,
    featuredItems,
    reviews,
    metrics,
    completionPercentage,
    updatePortfolioItem,
    addPortfolioItem,
    deletePortfolioItem
  };
  
  return (
    <PortfolioContext.Provider value={value}>
      {children}
    </PortfolioContext.Provider>
  );
};

/**
 * Custom hook to use the portfolio context
 * @returns {Object} Portfolio context value
 */
export const usePortfolio = () => {
  const context = useContext(PortfolioContext);
  if (!context) {
    throw new Error('usePortfolio must be used within a PortfolioProvider');
  }
  return context;
};

export default PortfolioContext;

